/**
 * Helpers for scoping center/location lists to the current user's assignments.
 */

export function isSuperAdminUser(currentUser) {
  if (!currentUser) return false;
  if (currentUser.isSuperAdmin) return true;
  if (currentUser?.roles?.some((role) => role?.name === "Super Admin")) {
    return true;
  }
  return Boolean(
    currentUser?.userRoles?.some(
      (userRole) => userRole?.role?.name === "Super Admin"
    )
  );
}

/** Center Admin role (not Super Admin). */
export function isAdminUser(currentUser) {
  if (!currentUser) return false;
  if (currentUser?.roles?.some((role) => role?.name === "Admin")) {
    return true;
  }
  return Boolean(
    currentUser?.userRoles?.some(
      (userRole) => userRole?.role?.name === "Admin"
    )
  );
}

/** Super Admin or center Admin — may manage the full SAR intake queue. */
export function canManageSubjectAccessRequests(currentUser) {
  return isSuperAdminUser(currentUser) || isAdminUser(currentUser);
}

export function getUserAssignedCenterIds(currentUser) {
  const ids = Array.isArray(currentUser?.locationIds) ? currentUser.locationIds : [];
  return new Set(ids.map((id) => String(id)));
}

/**
 * Restrict a centers list to locations the user is assigned to.
 * Super Admins see all centers; everyone else sees only assigned centers.
 */
export function filterCentersByUserAssignment(centers, currentUser) {
  if (!Array.isArray(centers)) return [];
  if (isSuperAdminUser(currentUser)) return centers;

  const allowed = getUserAssignedCenterIds(currentUser);
  if (allowed.size === 0) return [];

  return centers.filter((center) => center?.id != null && allowed.has(String(center.id)));
}

export function toCenterSelectOptions(centers) {
  return (centers || []).map((location) => ({
    id: location.id,
    label: location.location_name || location.name || "Unknown",
    ...location,
  }));
}

/** Roles that belong to the currently selected center (or global/default). */
export function filterRolesForCurrentCenter(roles, currentUser) {
  if (!Array.isArray(roles)) return [];
  const locationId =
    currentUser?.currentLocationId ?? currentUser?.currentLocation?.id ?? null;
  if (!locationId) return roles;

  return roles.filter((role) => {
    const catalogLoc = role?.location_id ?? null;
    const assignmentLoc = role?.assignment_location_id ?? role?.userRoles?.location_id ?? null;
    if (catalogLoc && String(catalogLoc) !== String(locationId)) return false;
    if (assignmentLoc && String(assignmentLoc) !== String(locationId)) return false;
    return true;
  });
}

/** Label for a location_id on catalog/management rows. */
export function getCenterLabel(locationId, centers = []) {
  if (locationId == null || locationId === "") return "All Centers";
  const match = (centers || []).find(
    (center) => String(center.id) === String(locationId)
  );
  return match?.name || match?.location_name || match?.label || "Unknown center";
}

export function getManagementLocationParams(currentUser, overrideLocationId) {
  if (isSuperAdminUser(currentUser) && !overrideLocationId) {
    const activeId =
      currentUser?.currentLocationId ?? currentUser?.currentLocation?.id ?? null;
    return activeId ? { location_id: activeId } : {};
  }

  const locationId =
    overrideLocationId ??
    currentUser?.currentLocationId ??
    currentUser?.currentLocation?.id ??
    null;

  return locationId ? { location_id: locationId } : {};
}

/** location_id for device list APIs (center-scoped list + lazy seed on backend). */
export function getDeviceLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for medication list APIs (center-scoped list + lazy seed on backend). */
export function getMedicationLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for medication hold timing list APIs (center-scoped list + lazy seed on backend). */
export function getMedicationHoldTimingLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for medication frequency list APIs (center-scoped list + lazy seed on backend). */
export function getMedicationFrequencyLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for insurance list APIs (center-scoped list + lazy seed on backend). */
export function getInsuranceLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for indication list APIs (center-scoped list + lazy seed on backend). */
export function getIndicationLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for specialty list APIs (center-scoped list + lazy seed on backend). */
export function getSpecialtyLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}

/** location_id for complication list APIs (center-scoped list + lazy seed on backend). */
export function getComplicationLocationParams(centerLocationId, currentUser) {
  return getManagementLocationParams(currentUser, centerLocationId);
}
